import Throttle from '@/js/utils/Throttle';

/**
 * A class to automatically manage menu items that overflow their container width.
 * h/t https://codepen.io/lukejacksonn/pen/PwmwWV
 */
export default class PriorityNav {
  /**
   * Create a PriorityNav instance.
   * @constructor
   *
   * @param {object} config The config object.
   */
  constructor(config) {
    /**
     * Component configuration options.
     *
     * @type {object}
     */
    const options = {
      /**
       * The element wrapping the menu.
       *
       * @type {HTMLElement}
       */
      wrapper: null,

      /**
       * The menu element.
       *
       * @type {HTMLUListElement}
       */
      menu: null,

      /**
       * The overflow menu toggle.
       *
       * @type {HTMLButtonElement}
       */
      button: null,

      /**
       * The overflow menu.
       *
       * @type {HTMLUListElement}
       */
      overflow: null,

      /**
       * Callback to run after the overflow menu is initialized.
       *
       * @callback initCallback
       */
      onInit: () => {},

      /**
       * Callback to run after the overflow menu is updated.
       *
       * @callback updateCallback
       */
      onUpdate: () => {},

      /**
       * Callback to run after the overflow menu is destroyed.
       *
       * @callback destroyCallback
       */
      onDestroy: () => {},
    };

    // Bind class methods.
    this.init = this.init.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.destroy = this.destroy.bind(this);

    // Filter out null & undefined values.
    const invalueConfig = Object.keys(config).filter((item) => !config[item]);

    // Only proceed if we have everything we need.
    if (invalueConfig.length === 0) {
      // Merge config options with defaults.
      Object.assign(this, options, config);

      this.init();
    }
  }

  /**
   * Initialize the component.
   */
  init() {
    /**
     * The width at which items are moved to the overflow menu. Used to decide
     * if an item can be moved back to the original location.
     *
     * @type {Array}
     */
    this.breakWidths = [];

    /**
     * The overflow button and list wrapper element.
     */
    this.overflowWrapper = this.button.parentElement;

    // Kick off the component.
    this.handleResize();
    this.resize = new Throttle(window, 'resize', this.handleResize);

    // Run {initCallback}
    this.onInit.call(this, this.overflow.children.length);
  }

  /**
   * Handle resize events.
   */
  handleResize() {
    // Total available space for menu items.
    const availableSpace = (this.overflowWrapper.hasAttribute('hidden'))
      ? this.wrapper.offsetWidth
      : this.wrapper.offsetWidth - this.button.offsetWidth;

    if (this.menu.offsetWidth > availableSpace) {
      const lastMenuItem = this.overflowWrapper.previousElementSibling;
      // Attempt to move the last non-overflow item to the overflow menu.
      if (lastMenuItem !== null) {
        this.breakWidths.push(this.menu.offsetWidth);

        // Move the item to the overflow menu.
        this.overflow.insertBefore(
          lastMenuItem,
          this.overflow.firstElementChild,
        );

        // Show the overflow button.
        this.overflowWrapper.removeAttribute('hidden');
      }
    } else {
      if (availableSpace > this.breakWidths[this.breakWidths.length - 1]) {
        // Move the item back to the menu.
        this.menu.insertBefore(
          this.overflow.firstElementChild,
          this.overflowWrapper,
        );

        // Get rid of the associated break width.
        this.breakWidths.pop();
      }

      // Hide the overflow if hidden list is empty.
      if (
        this.breakWidths.length === 0
        || this.overflow.firstElementChild === null
      ) {
        this.overflowWrapper.setAttribute('hidden', '');
      }
    }

    // Recur if the visible list is still overflowing the menu.
    if (this.menu.offsetWidth > availableSpace) {
      this.handleResize();
    } else {
      // Run {updateCallback}
      this.onUpdate.call(this, this.overflow.children.length);
    }
  }

  /**
   * Destroy this component.
   */
  destroy() {
    this.breakWidths = [];

    if (this.resize instanceof Throttle) {
      this.resize.destroy();
    }

    this.overflowWrapper.setAttribute('hidden', '');

    // Move overflow items back to the menu.
    Array.from(this.overflow.children).forEach((element) => {
      this.menu.insertBefore(element, this.overflowWrapper);
    });

    // Run {destroyCallback}
    this.onDestroy.call(this);
  }
}
